/**
 * SRAM and WRAM layout (ARCHITECTURE §4), derived from pokered.sym.
 * SRAM file offset = bank * 0x2000 + (addr - 0xA000).
 * Fields inside sMainData sit at sMainData + (wAddr - wMainDataStart), because
 * SaveMainData copies wMainDataStart..wMainDataEnd verbatim.
 */

export const SRAM_SIZE = 0x8000;
export const SRAM_BANK_SIZE = 0x2000;

export function sramOffset(bank: number, addr: number): number {
  return bank * SRAM_BANK_SIZE + (addr - 0xa000);
}

/** WRAM / HRAM addresses (identical in Red and Blue). */
export const W = {
  wSpriteDataStart: 0xc100,
  wSpriteDataEnd: 0xc300,
  wPlayerName: 0xd158,
  wPartyDataStart: 0xd163,
  wPartyCount: 0xd163,
  wPartySpecies: 0xd164,
  wPartyMons: 0xd16b,
  wPartyMonOT: 0xd273,
  wPartyMonNicks: 0xd2b5,
  wPartyDataEnd: 0xd2f7,
  wMainDataStart: 0xd2f7,
  wPokedexOwned: 0xd2f7,
  wPokedexSeen: 0xd30a,
  wNumBagItems: 0xd31d,
  wBagItems: 0xd31e,
  wPlayerMoney: 0xd347,
  wRivalName: 0xd34a,
  wOptions: 0xd355,
  wObtainedBadges: 0xd356,
  wPlayerID: 0xd359,
  wCurMap: 0xd35e,
  wNumBoxItems: 0xd53a,
  wBoxItems: 0xd53b,
  wCurrentBoxNum: 0xd5a0,
  wNumHoFTeams: 0xd5a2,
  wPlayerCoins: 0xd5a4,
  /** ROM patch 2 (§3.5) counters in the unused bytes after wGameProgressFlagsEnd. */
  rcDayCarePaid: 0xd6b8,
  rcTrainedLevels: 0xd6bc,
  /** ROM patch 3 (rom/qol) bag pockets, in bytes retail leaves unused and zero. */
  wTmQuantities: 0xd6be,
  wHmFlags: 0xd702,
  wBallQuantities: 0xd703,
  /** Start menu WALLET handshake (rom/qol); client clears after closing the Wallet UI. */
  wRedChainWalletReq: 0xd708,
  /** Cable Club board handshake. The byte after the wallet request; old saves leave it 0. */
  wRedChainBoardReq: 0xd709,
  /** 0 none, 1 Trade Center, 2 Colosseum. The pad byte after the board request. */
  wRedChainUnionDest: 0xd70a,
  wEventFlags: 0xd747,
  wPlayTimeHours: 0xda41,
  wPlayTimeMaxed: 0xda42,
  wPlayTimeMinutes: 0xda43,
  wPlayTimeSeconds: 0xda44,
  wPlayTimeFrames: 0xda45,
  wDayCareInUse: 0xda48,
  wDayCareMonName: 0xda49,
  wDayCareMonOT: 0xda54,
  wDayCareMon: 0xda5f,
  wMainDataEnd: 0xda80,
  wBoxDataStart: 0xda80,
  wBoxCount: 0xda80,
  wBoxDataEnd: 0xdee2,
  hTileAnimations: 0xffd7,
} as const;

/** Game constants (constants/*.asm). */
export const NAME_LENGTH = 11;
export const PARTY_LENGTH = 6;
export const MONS_PER_BOX = 20;
export const NUM_BOXES = 12;
export const BOXES_PER_BANK = 6;
export const BAG_ITEM_CAPACITY = 20;
export const PC_ITEM_CAPACITY = 50;
export const MAX_ITEM_QTY = 99;
export const BOX_STRUCT_LENGTH = 33;
export const PARTY_STRUCT_LENGTH = 44;
export const BOX_NUM_MASK = 0x7f;
export const BIT_HAS_CHANGED_BOXES = 7;
export const POKEDEX_BYTES = 19;
export const EVENT_FLAG_BYTES = 320;
export const TERMINATOR = 0xff;
export const TEXT_TERMINATOR = 0x50;

/** Bank 1 save data (sGameData .. sMainDataCheckSum). */
export const S = {
  sGameData: sramOffset(1, 0xa598), // 0x2598
  sPlayerName: sramOffset(1, 0xa598), // 0x2598
  sMainData: sramOffset(1, 0xa5a3), // 0x25A3
  sSpriteData: sramOffset(1, 0xad2c), // 0x2D2C
  sPartyData: sramOffset(1, 0xaf2c), // 0x2F2C
  sCurBoxData: sramOffset(1, 0xb0c0), // 0x30C0
  sTileAnimations: sramOffset(1, 0xb522), // 0x3522
  sGameDataEnd: sramOffset(1, 0xb523), // 0x3523 (exclusive end)
  sMainDataCheckSum: sramOffset(1, 0xb523), // 0x3523
  sHallOfFame: sramOffset(0, 0xa598), // 0x0598
} as const;

/**
 * SRAM bytes before this offset are bank 0's sprite decompression buffers: the game rewrites them
 * whenever it draws a Pokémon picture, so they never mean a save happened.
 */
export const SRAM_SCRATCH_END = S.sHallOfFame;

export const MAIN_DATA_LENGTH = W.wMainDataEnd - W.wMainDataStart; // 0x789 = 1929
export const SPRITE_DATA_LENGTH = W.wSpriteDataEnd - W.wSpriteDataStart; // 0x200
export const PARTY_DATA_LENGTH = W.wPartyDataEnd - W.wPartyDataStart; // 0x194 = 404
export const BOX_DATA_LENGTH = W.wBoxDataEnd - W.wBoxDataStart; // 0x462 = 1122

/** SRAM offset of a WRAM main-data field. */
export function mainField(wAddr: number): number {
  if (wAddr < W.wMainDataStart || wAddr >= W.wMainDataEnd) {
    throw new RangeError(`0x${wAddr.toString(16)} is outside wMainData`);
  }
  return S.sMainData + (wAddr - W.wMainDataStart);
}

export const F = {
  playerName: S.sPlayerName,
  pokedexOwned: mainField(W.wPokedexOwned), // 0x25A3
  pokedexSeen: mainField(W.wPokedexSeen), // 0x25B6
  numBagItems: mainField(W.wNumBagItems), // 0x25C9
  bagItems: mainField(W.wBagItems), // 0x25CA
  money: mainField(W.wPlayerMoney), // 0x25F3
  rivalName: mainField(W.wRivalName), // 0x25F6
  options: mainField(W.wOptions), // 0x2601
  badges: mainField(W.wObtainedBadges), // 0x2602
  playerId: mainField(W.wPlayerID), // 0x2605
  curMap: mainField(W.wCurMap), // 0x260A
  numBoxItems: mainField(W.wNumBoxItems), // 0x27E6
  boxItems: mainField(W.wBoxItems), // 0x27E7
  currentBoxNum: mainField(W.wCurrentBoxNum), // 0x284C
  numHoFTeams: mainField(W.wNumHoFTeams), // 0x284E
  coins: mainField(W.wPlayerCoins), // 0x2850
  dayCarePaid: mainField(W.rcDayCarePaid), // 0x2964
  trainedLevels: mainField(W.rcTrainedLevels), // 0x2968
  tmQuantities: mainField(W.wTmQuantities), // 0x296A
  hmFlags: mainField(W.wHmFlags), // 0x29AE
  ballQuantities: mainField(W.wBallQuantities), // 0x29AF
  eventFlags: mainField(W.wEventFlags), // 0x29F3
  playTimeHours: mainField(W.wPlayTimeHours), // 0x2CED
  playTimeMaxed: mainField(W.wPlayTimeMaxed), // 0x2CEE
  playTimeMinutes: mainField(W.wPlayTimeMinutes), // 0x2CEF
  playTimeSeconds: mainField(W.wPlayTimeSeconds), // 0x2CF0
  playTimeFrames: mainField(W.wPlayTimeFrames), // 0x2CF1
  dayCareInUse: mainField(W.wDayCareInUse), // 0x2CF4
  dayCareMonName: mainField(W.wDayCareMonName), // 0x2CF5
  dayCareMonOT: mainField(W.wDayCareMonOT), // 0x2D00
  dayCareMon: mainField(W.wDayCareMon), // 0x2D0B
} as const;

/** Relative offsets inside the party block (wPartyDataStart-relative). */
export const PARTY = {
  count: 0,
  species: W.wPartySpecies - W.wPartyDataStart, // 1
  mons: W.wPartyMons - W.wPartyDataStart, // 8
  ot: W.wPartyMonOT - W.wPartyDataStart, // 0x110
  nicks: W.wPartyMonNicks - W.wPartyDataStart, // 0x152
} as const;

/** Relative offsets inside a box block (wBoxDataStart-relative). */
export const BOX = {
  count: 0,
  species: 1,
  mons: 0xda96 - W.wBoxDataStart, // 0x16
  ot: 0xdd2a - W.wBoxDataStart, // 0x2AA
  nicks: 0xde06 - W.wBoxDataStart, // 0x386
} as const;

/** Stored box banks (sBox1..sBox12) and their checksums. */
export const BANKS = [
  {
    bank: 2,
    firstBox: 0,
    start: sramOffset(2, 0xa000), // 0x4000
    allChecksum: sramOffset(2, 0xba4c), // 0x5A4C
    boxChecksums: sramOffset(2, 0xba4d), // 0x5A4D
  },
  {
    bank: 3,
    firstBox: 6,
    start: sramOffset(3, 0xa000), // 0x6000
    allChecksum: sramOffset(3, 0xba4c), // 0x7A4C
    boxChecksums: sramOffset(3, 0xba4d), // 0x7A4D
  },
] as const;

/** SRAM offset of stored box `box` (0-based, 0..11). Ignored for the current box (§4.3). */
export function storedBoxOffset(box: number): number {
  if (!Number.isInteger(box) || box < 0 || box >= NUM_BOXES) throw new RangeError(`box ${box}`);
  const bank = BANKS[box < BOXES_PER_BANK ? 0 : 1];
  return bank.start + (box % BOXES_PER_BANK) * BOX_DATA_LENGTH;
}

/** Box struct field offsets (§4.4). */
export const MON = {
  species: 0,
  hp: 1,
  boxLevel: 3,
  status: 4,
  type1: 5,
  type2: 6,
  catchRate: 7,
  moves: 8,
  otId: 12,
  exp: 14,
  statExp: 17, // hp, atk, def, spd, spc; u16 BE each
  dvs: 27,
  pp: 29,
  level: 33, // party only
  stats: 34, // party only: maxHp, atk, def, spd, spc; u16 BE each
} as const;
